// src/components/intake/ProofPreview.jsx
//
// The proof: the author's book as it will print, shown on the Review step,
// with the tick that says "print it like this". Submitting waits for the tick.
//
// A ReVisionari book is printed fresh at the ordered size by the same printer
// the team's copy comes from (GET /api/intake/preview/<id>). An uploaded PDF
// is printed as it is, so it is its own proof and needs no request.
// The tick can only be given once the preview has been opened.

import React, { useEffect, useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, Link, Typography } from '@mui/material';
import { tokens } from '../../theme';
import { intakeApi } from './api';
import { SP } from './intakeSx';

export default function ProofPreview({ draftId, file, size, confirmed, onConfirm }) {
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Another book, file or size makes the old preview stale; the parent clears
  // the tick for the same reason.
  useEffect(() => {
    setUrl(null);
    setError(null);
  }, [draftId, file, size]);
  useEffect(() => () => url && URL.revokeObjectURL(url), [url]);

  const open = async () => {
    setError(null);
    if (file) {
      setUrl(URL.createObjectURL(file));
      return;
    }
    setBusy(true);
    try {
      setUrl(await intakeApi.previewUrl(draftId, size));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ mt: 3 }} data-testid="intake-proof">
      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>Your proof</Typography>
      <Typography sx={{ fontSize: 13, color: SP.gray, mt: 0.5, maxWidth: '60ch' }}>
        {file
          ? 'Your PDF is printed exactly as it is. Look through it once more before you submit.'
          : 'Your book as it will print at the size you chose, with its contents page and page numbers. A long book can take a minute.'}
      </Typography>

      {!url && (
        <Button
          variant="outlined"
          onClick={open}
          disabled={busy}
          sx={{ mt: 1.5, textTransform: 'none' }}
          data-testid="intake-proof-open"
        >
          {busy ? 'Preparing your preview…' : file ? 'Preview your PDF' : 'Preview the printed book'}
        </Button>
      )}
      {error && (
        <Typography role="alert" sx={{ color: tokens.color.error, fontSize: 13, mt: 1.5 }}>
          {error}
        </Typography>
      )}
      {url && (
        <>
          <Box
            component="iframe"
            title="Book preview"
            src={url}
            sx={{
              display: 'block',
              width: '100%',
              height: '70vh',
              mt: 1.5,
              border: `1px solid ${SP.lineSoft}`,
              borderRadius: '6px',
              background: SP.white,
            }}
          />
          {/* Phones often show only the first page inside a page; a tab of its own shows them all. */}
          <Link href={url} target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-block', mt: 1, fontSize: 12.5 }}>
            Open it in a new tab
          </Link>
        </>
      )}

      <FormControlLabel
        sx={{ display: 'flex', alignItems: 'flex-start', mt: 1.5, mr: 0 }}
        control={
          <Checkbox
            checked={confirmed}
            onChange={(e) => onConfirm(e.target.checked)}
            disabled={!url}
            inputProps={{ 'data-testid': 'intake-proof-confirm' }}
            sx={{ pt: 0.25 }}
          />
        }
        label={
          <Typography sx={{ fontSize: 13, color: url ? SP.black : SP.gray }}>
            I&rsquo;ve checked the preview. This is how I want my book printed.
          </Typography>
        }
      />
    </Box>
  );
}
